import {
  Component,
  computed,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  Signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountGroup } from '@galaxy/account-group';
import { BillingStrategy, CreditBalance, CreditService, RetailCustomerConfiguration } from '@galaxy/billing';
import {
  InventoryItemWithPricing,
  ItemPricingTableConfig,
  ItemsChangedEvent,
  LineItem as InventoryLineItem,
  LineItemCacheService,
} from '@galaxy/inventory-ui';
import { FeatureFlagService } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { getPriceValueForEdition } from '@vendasta/billing-ui';
import { ORDER_BUSINESS_CONFIG_TOKEN, OrderFulfillmentServiceInterfaceToken } from '@vendasta/orders';
import {
  BillingPeriod,
  Cost,
  DurationInterface,
  DurationPeriod,
  LineItem as OrderLineItem,
  Order,
  Revenue,
} from '@vendasta/sales-orders';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { map, shareReplay, skipWhile, switchMap, take } from 'rxjs/operators';
import { AccountService } from '../../../core/account.service';
import { FeatureFlags } from '../../../core/features';
import { User } from '../../../core/user';
import { PreviewEmailDialogComponent } from '../../../preview-email-dialog/preview-email-dialog.component';
import { VConfigService } from '../../../vconfig/vconfig.service';
import { AppIdToEditionIdToEditionMap, isValidEditionId } from '../../core/interface';
import { ProductOrderFormStoreService } from '../../core/product-order-form.store.service';
import {
  updateOrderCustomPriceFromItem,
  updatePricingTableItemsWithCustomPrice,
} from '../../order-form-page/custom-price.util';
import { OrderConfirmationItem } from '../interface';
import { FulfillmentOrderConfirmationService } from './fulfillment-order-confirmation.service';
import { formatApprovalMessage, getBilledOrderItemsForStrategy } from './order-summary.helper';

const emailSmbsProductActivatedKey = 'email_smbs_product_activated';

export interface OrderSubmission {
  order: Order;
  sendEmail: boolean;
}

export interface AppNameAndEditionNamePair {
  appName: string;
  editionName?: string;
}

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  providers: [
    FulfillmentOrderConfirmationService,
    {
      provide: OrderFulfillmentServiceInterfaceToken,
      useExisting: FulfillmentOrderConfirmationService,
    },
  ],
  standalone: false,
})
export class OrderSummaryComponent implements OnInit, OnChanges {
  private readonly orderBusinessConfig = inject(ORDER_BUSINESS_CONFIG_TOKEN);
  private readonly cachedSalesOrder: Signal<Order>;
  private readonly cachedInventoryLineItems: WritableSignal<InventoryLineItem[]>;
  readonly editableOrder: WritableSignal<Order>;
  readonly pricingTableItems: Signal<InventoryItemWithPricing[]>;
  readonly minDate = new Date(new Date().setHours(7));
  private readonly injector = inject(Injector);

  @Input()
  set orderConfirmationItems(orderItems: OrderConfirmationItem[]) {
    orderItems = orderItems || [];
    const appNamesThatRequireApproval = this.assembleAppNamesForAppsThatRequireApproval(orderItems);
    this.approvalMessageCount = appNamesThatRequireApproval.length;
    this.orderContainsItemsThatAreNotFree = orderItems.some(
      (item) =>
        item.appPrices &&
        getPriceValueForEdition(
          item.appPrices.pricesForContexts['wholesale'],
          item.partnerCurrency,
          item.editionId || '',
          true,
        ) > 0,
    );
    this.approvalMessage = formatApprovalMessage(appNamesThatRequireApproval);
    this.orderItems = orderItems;

    const instantBilledOrderItems = getBilledOrderItemsForStrategy(
      this.orderItems,
      this.productIsInstantBilledMap,
      BillingStrategy.Instantly,
    );
    this.instantActivationItemSummaries = this.buildActivationItemSummaries(instantBilledOrderItems, this.editionsMap);
    const nonInstantBilledOrderItems = this.orderItems.filter(
      (item) => !instantBilledOrderItems.find((i) => i.appId === item.appId && i.addonId === item.addonId),
    );
    this.activationItemSummaries = this.buildActivationItemSummaries(nonInstantBilledOrderItems, this.editionsMap);
    this.fulfillmentOrderConfirmationService.setOrderItems(
      Array.from(new Set([...this.instantActivationItemSummaries, ...this.activationItemSummaries])),
    );
    this.allActivationSummaries = this.buildActivationItemSummaries(orderItems, this.editionsMap);
  }

  @Input() retailCustomerConfiguration!: RetailCustomerConfiguration;
  @Input() productIsInstantBilledMap: Map<string, BillingStrategy>;
  @Input() userCanEditBillingInfo: boolean;
  @Input() stairStepOrderRequiresBilling: boolean;
  @Input() partnerId: string;
  @Input() billingInfoConfigured: boolean;
  @Input() submitDisabled: boolean;
  @Input() accountGroupId: string;
  @Input() hidePricing: boolean;
  @Input() editionsMap: AppIdToEditionIdToEditionMap;
  @Input() billingAutomationEnabled: boolean;
  @Input() marketCurrency: string;
  @Input() business: AccountGroup;

  @Output() submitClicked = new EventEmitter<OrderSubmission>();
  @Output() goBackClicked = new EventEmitter<void>();

  @Input() requireApproval!: boolean;
  @Output() requireApprovalChange = new EventEmitter<boolean>();

  @Input() approvalFromSubjectId: string;
  @Output() approvalFromSubjectIdChange = new EventEmitter<string>();

  @Input() hasExpiryDate!: boolean;
  @Output() hasExpiryDateChange = new EventEmitter<boolean>();

  @Input() expiryDate: Date;
  @Output() expiryDateChange = new EventEmitter<Date>();

  @Input() contractStartDate = this.minDate;
  @Output() contractStartDateChange = new EventEmitter<Date>();

  private _contractDuration: DurationInterface;
  @Input()
  get contractDuration(): DurationInterface {
    return this._contractDuration;
  }
  set contractDuration(value: DurationInterface | null) {
    if (value) {
      this._contractDuration = value;
    } else {
      const defaultDuration = { value: 1, duration: DurationPeriod.YEAR };
      this._contractDuration = defaultDuration;
      this.contractDurationChange.emit(this._contractDuration);
    }
  }
  @Output() contractDurationChange = new EventEmitter<DurationInterface>();

  get activatingProductDisabled(): boolean {
    return (
      (!this.billingInfoConfigured &&
        this.instantActivationItemSummaries.length > 0 &&
        this.orderContainsItemsThatAreNotFree) ||
      (!this.billingInfoConfigured && this.stairStepOrderRequiresBilling)
    );
  }

  orderItems: OrderConfirmationItem[];
  orderContainsItemsThatAreNotFree: boolean;
  approvalMessage: string;
  approvalMessageCount: number;
  productIds: string[] = [];
  emailDisabled = true;
  emailUsersChecked: boolean;
  instantActivationItemSummaries: OrderConfirmationItem[] = [];
  activationItemSummaries: OrderConfirmationItem[] = [];
  allActivationSummaries: OrderConfirmationItem[] = [];

  users$: Observable<User[]>;
  creditBalance$: Observable<CreditBalance>;
  dialogRef: MatDialogRef<PreviewEmailDialogComponent>;
  minStartDate: Date = new Date();

  marketId$: Observable<string>;
  valid = true;
  hasSufficientVCash$: Observable<boolean>;
  wholesaleTotal$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  showContractDetails$: Observable<boolean>;
  pricingTableConfig: Signal<ItemPricingTableConfig | null>;
  accountLink$: Observable<string>;

  constructor(
    private accountService: AccountService,
    private vconfigService: VConfigService,
    private creditService: CreditService,
    private productOrderFormService: ProductOrderFormStoreService,
    private fulfillmentOrderConfirmationService: FulfillmentOrderConfirmationService,
    private lineItemCacheService: LineItemCacheService,
    public dialog: MatDialog,
    private readonly featureFlagService: FeatureFlagService,
    private readonly translateService: TranslateService,
  ) {
    this.cachedSalesOrder = toSignal(this.productOrderFormService.cachedOrder$);
    this.cachedInventoryLineItems = signal(this.lineItemCacheService.readLineItems(this.accountGroupId));
    this.editableOrder = signal(
      buildEditableOrder(this.cachedSalesOrder(), this.cachedInventoryLineItems(), this.marketCurrency),
    );
    this.pricingTableItems = computed(() => {
      const cachedLineItems = this.cachedInventoryLineItems();
      const editableOrder = this.editableOrder();
      const pricingTableItems = this.buildPricingTableItemsFromLineItems(cachedLineItems);
      return updatePricingTableItemsWithCustomPrice(pricingTableItems, editableOrder);
    });
  }

  ngOnInit(): void {
    const configKeys = [emailSmbsProductActivatedKey];
    this.accountService.account$
      .pipe(
        skipWhile((account) => !account.market_id),
        switchMap((account) => this.vconfigService.getConfigurations(this.partnerId, configKeys, account.market_id)),
        take(1),
      )
      .subscribe((config) => {
        this.emailUsersChecked = config[emailSmbsProductActivatedKey];
        this.emailDisabled = false;
      });

    this.creditBalance$ = this.creditService.getBalance(this.partnerId).pipe(shareReplay(1));
    this.users$ = this.accountService.users$.pipe(shareReplay(1));

    this.hasSufficientVCash$ = combineLatest([this.creditBalance$, this.wholesaleTotal$$]).pipe(
      map(([balance, total]) => balance?.amount >= total),
    );

    this.accountLink$ = this.getAccountLink(this.accountGroupId);

    this.marketId$ = this.accountService.account$.pipe(
      skipWhile((account) => !account?.market_id),
      map((account) => account.market_id),
    );

    const features$ = this.marketId$.pipe(
      switchMap((marketId) =>
        this.featureFlagService.batchGetStatus(this.partnerId, marketId, [FeatureFlags.ACTIVATION_FLOW_CONTRACT_TERMS]),
      ),
    );

    this.showContractDetails$ = features$.pipe(map((res) => res[FeatureFlags.ACTIVATION_FLOW_CONTRACT_TERMS]));

    const pricingTableConfigDeps = toSignal(
      combineLatest([this.marketId$, features$]).pipe(
        map(([marketId, ff]) => {
          return {
            marketId,
            billingTermsEnabled: ff[FeatureFlags.ACTIVATION_FLOW_CONTRACT_TERMS],
          };
        }),
      ),
      { injector: this.injector },
    );

    this.pricingTableConfig = computed(() => {
      const deps = pricingTableConfigDeps();
      if (!deps) {
        return null;
      }

      return {
        partnerId: this.partnerId,
        marketId: deps.marketId,
        businessId: this.accountGroupId,
        billingTermsConfig: {
          enabled: deps.billingTermsEnabled,
          defaultStartDate: this.editableOrder().requestedActivation,
        },
        canAddItems: false,
      };
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['accountGroupId']) {
      this.cachedInventoryLineItems.set(this.lineItemCacheService.readLineItems(this.accountGroupId));
      this.editableOrder.set(
        buildEditableOrder(this.cachedSalesOrder(), this.cachedInventoryLineItems(), this.marketCurrency),
      );
    }
  }

  buildActivationItemSummaries(
    orderItems: OrderConfirmationItem[],
    editionsMap: AppIdToEditionIdToEditionMap,
  ): OrderConfirmationItem[] {
    if (!orderItems || orderItems.length === 0) {
      return [];
    }
    return orderItems.map((oi) => {
      if (this.orderConfirmationItemHasValidEditionId(oi)) {
        oi.editionName = editionsMap.get(oi.appId).get(oi.editionId).editionName;
      }
      return oi;
    });
  }

  buildPricingTableItemsFromLineItems(lineItems: InventoryLineItem[]): InventoryItemWithPricing[] {
    if (!lineItems?.length) {
      return [];
    }
    return lineItems.map((li): InventoryItemWithPricing => {
      return {
        itemId: li.itemId,
        edition: { editionId: li.editionId || '' },
        type: li.itemType,
        quantity: li.quantity,
      };
    });
  }

  async onSubmit(): Promise<void> {
    this.submitClicked.emit({ order: this.editableOrder(), sendEmail: this.emailUsersChecked });
  }

  goBack(): void {
    this.goBackClicked.emit();
  }

  presentPreviewEmailDialog(): void {
    this.dialogRef = this.dialog.open(PreviewEmailDialogComponent);
  }

  assembleAppNamesForAppsThatRequireApproval(orderItems: OrderConfirmationItem[]): AppNameAndEditionNamePair[] {
    const appNamesThatRequireApproval: AppNameAndEditionNamePair[] = [];
    orderItems.forEach((item) => {
      if (item.requiresApproval) {
        const namesPair = { appName: item.name } as AppNameAndEditionNamePair;
        if (this.orderConfirmationItemHasValidEditionId(item)) {
          namesPair.editionName = this.editionsMap.get(item.appId).get(item.editionId).editionName;
        }
        appNamesThatRequireApproval.push(namesPair);
      }
    });

    return appNamesThatRequireApproval;
  }

  orderConfirmationItemHasValidEditionId(item: OrderConfirmationItem): boolean {
    if (!item) {
      return false;
    }
    const { editionId, appId } = item;
    const itemHasValidEditionId = isValidEditionId(editionId);
    const editionsEntry = this.editionsMap.get(appId);
    const editionDataIsInEditionsMap = !!(editionsEntry && editionsEntry.get(editionId));
    return itemHasValidEditionId && editionDataIsInEditionsMap;
  }

  updateOrderItem(item: OrderConfirmationItem): void {
    this.editableOrder.update((order) => {
      updateOrderCustomPriceFromItem(order, item);
      return order;
    });
  }

  updateOrderItemsFromChangedEvent(event: ItemsChangedEvent): void {
    if (event.type !== 'update') return;

    this.editableOrder.update((order) => {
      event.items.forEach((item) => {
        const lineItem = order.lineItems[item.index];
        lineItem.cost = item.pricing?.cost as Cost;
        lineItem.currentRevenue = item.pricing.currentRevenue as Revenue;
        lineItem.billingPeriod = item.pricing.billingPeriod as BillingPeriod;
        lineItem.currencyCode = item.pricing.currencyCode;
        order.lineItems[item.index] = lineItem;
      });
      return order;
    });
  }

  handleContractStartDateChange(date: Date): void {
    this.editableOrder.update((order) => {
      return <Order>{
        ...order,
        requestedActivation: date,
      };
    });
    this.contractStartDateChange.emit(date);
  }

  handleContractDurationChange(contractDuration: DurationInterface): void {
    this.editableOrder.update((order) => {
      return <Order>{
        ...order,
        contractDuration: contractDuration,
      };
    });
    this.contractDurationChange.emit(contractDuration);
  }

  setValidity(valid: boolean): void {
    this.valid = valid;
  }

  setWholesaleTotal(total: number): void {
    this.wholesaleTotal$$.next(total);
  }

  private getAccountLink(accountGroupId: string): Observable<string> {
    const url = this.orderBusinessConfig.getBusinessUrl(accountGroupId);
    if (url instanceof Promise) {
      return from(url);
    }
    return of(url);
  }
}

export function buildEditableOrder(
  cachedOrder: Order,
  cachedLineItems: InventoryLineItem[],
  marketCurrency: string,
): Order {
  if (!cachedOrder || !cachedLineItems?.length) {
    return cachedOrder;
  }

  // Copy the cached order to create the base of the editable order
  const editableOrder = JSON.parse(JSON.stringify(cachedOrder));
  const cachedOrderLineItemsMap = cachedOrder.lineItems.reduce((map, lineItem) => {
    map.set(lineItem.appKey?.appId || lineItem.packageId, lineItem);
    return map;
  }, new Map<string, OrderLineItem>());
  // Merge the cached order and cached line items into an editable order
  editableOrder.lineItems = cachedLineItems
    .map((cachedLineItem) => {
      const orderLineItem = cachedOrderLineItemsMap.get(cachedLineItem.itemId);
      if (cachedLineItem.itemType === 'package') {
        return new OrderLineItem({
          packageId: cachedLineItem.itemId,
          quantity: cachedLineItem.quantity,
          currencyCode: marketCurrency,
        });
      }
      return orderLineItem;
    })
    .filter(Boolean);
  return editableOrder;
}
